import { useEffect, useState } from "react";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import { fetchPublications } from "@/lib/api";
import { Search, FileText, Download, BookOpen } from "lucide-react";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "foundational", label: "Foundational" },
  { id: "research", label: "Research" },
  { id: "practice", label: "Practice" },
  { id: "essays", label: "Essays" },
  { id: "standards", label: "Standards" },
];

export default function Research() {
  const [pubs, setPubs] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPublications({ category: category === "all" ? undefined : category, q: q || undefined })
      .then(setPubs)
      .finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div data-testid="research-page">
      <PageHero
        testId="research-hero"
        eyebrow="Research & Publications"
        title="A digital library of the discipline."
        lead="Foundational monographs, peer-reviewed research, standards, essays and practitioner handbooks. Together, the working canon of cleanability."
      />

      {/* filter bar */}
      <section className="container-x border-t border-border py-6 sticky top-16 md:top-20 z-30 bg-background/85 backdrop-blur-xl">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="research-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`research-cat-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`px-3.5 h-9 rounded-md text-xs font-medium border transition-colors ${
                  category === c.id
                    ? "bg-foreground text-background border-foreground"
                    : "border-border bg-card text-foreground/80 hover:border-[hsl(var(--accent))]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="research-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search titles, authors, topics…"
              className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
            />
          </div>
        </div>
      </section>

      {/* list */}
      <section className="container-x py-16">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading the library…</div>
        ) : pubs.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <BookOpen className="h-8 w-8 mx-auto mb-4" />
            No publications match the current filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {pubs.map((pub, i) => (
              <Reveal key={pub.id} delay={i * 0.03}>
                <article data-testid={`pub-${i}`} className="bg-card p-8 md:p-10 hover-lift h-full flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="font-mono-mini text-[hsl(var(--accent))]">{pub.type}</div>
                    <div className="font-mono-mini text-muted-foreground">{pub.year}</div>
                  </div>
                  <h3 className="mt-4 font-display text-xl md:text-2xl font-medium tracking-tight text-balance">{pub.title}</h3>
                  <div className="mt-2 text-sm text-muted-foreground">{pub.author}</div>
                  <p className="mt-5 text-sm text-muted-foreground leading-relaxed flex-1">{pub.abstract}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {(pub.tags || []).map((t) => (
                      <span key={t} className="text-[10px] font-mono-mini px-2 py-1 rounded-md border border-border text-muted-foreground">{t}</span>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-border flex items-center justify-between text-sm">
                    <div className="text-xs text-muted-foreground">{pub.pages ? `${pub.pages} pages` : ""}</div>
                    <div className="flex items-center gap-3">
                      {pub.pdf_path ? (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pub.pdf_path}`} target="_blank" rel="noreferrer" data-testid={`pub-pdf-${i}`} className="inline-flex items-center gap-1.5 font-medium hover:text-[hsl(var(--accent))]">
                          <Download className="h-3.5 w-3.5" /> PDF
                        </a>
                      ) : (
                        <span data-testid={`pub-pdf-${i}`} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground/60">
                          <Download className="h-3.5 w-3.5" /> PDF
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
